import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Loader2, type LucideIcon } from "lucide-react";
import { useSession } from "@/frontend/auth-client";
import { orpc, orpcClient } from "@/frontend/orpc-client";
import {
  ConsentDisclosure,
  ConsentTermsLine,
} from "@/frontend/components/consent-flow/ConsentDisclosure";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/frontend/components/ui/select";
import { cn } from "@/frontend/lib/utils";
import { generateUniqueSlug } from "@/frontend/utils/shared";
import { requesterAddressHeading, workspaceSetupCopy } from "@/frontend/utils/org";
import {
  ACCOUNT_TYPE,
  DESIGNATIONS,
  PERSONA_ICON,
  PERSONA_OPTIONS,
  PERSONA_QUESTION,
  PERSONA_SUBHEADING,
  REQUESTER_ICON,
  REQUESTER_OPTIONS,
  REQUESTER_QUESTION,
  REQUESTER_TYPE,
  STEP,
  STEP_LABEL,
  WORKSPACE_NAME_LABEL,
  WORKSPACE_NAME_QUESTION,
  WORKSPACE_NAME_SUBHEADING,
  type AccountType,
  type ChoiceOption,
  type Designation,
  type FieldSpan,
  type RequesterType,
  type Step,
} from "@/frontend/types/org";

type ExistingWorkspace = {
  id: string;
  name: string;
};

type Props = {
  /**
   * Present only in backfill mode, holding the workspace that already exists and
   * needs its persona recorded. Absent means a new workspace is being created,
   * either the member's first at signup or another one from the switcher.
   */
  existingWorkspace?: ExistingWorkspace;
  /** Existing patients across the backfilled workspace group. */
  existingPatientCount?: number;
  /** Called after the persona is recorded and the workspace exists. */
  onCompleted?: () => void;
  /**
   * Renders a way out of step one. Supplied where the flow is optional (the
   * switcher's "create workspace"); absent where a workspace is mandatory.
   */
  onCancel?: () => void;
};

type ChoiceCardGroupProps<T extends string> = {
  /** Accessible name for the group, rendered visibly when `labelId` is given. */
  label: string;
  /** Set to render `label` above the cards and wire it as the group's name. */
  labelId?: string;
  options: readonly ChoiceOption<T>[];
  icons: Record<T, LucideIcon>;
  value: T | null;
  onSelect: (value: T) => void;
  disabled: boolean;
  /**
   * `tall` is the step-one persona pair, side by side. `compact` is the same
   * pair at card-row size. `list` is one bordered column of radio rows.
   */
  variant: "tall" | "compact" | "list";
};

/** Shared control sizing for every field in the details grid. */
const FIELD_CONTROL_CLASS = "h-9 w-full rounded-lg text-body md:text-body";

/** The six-column grid holding every workspace-details field. */
const DETAILS_GRID_CLASS = "grid gap-3 sm:grid-cols-6";

/** Six columns divide into halves and thirds, so both row shapes are exact. */
const FIELD_SPAN_CLASS: Record<FieldSpan, string> = {
  third: "sm:col-span-2",
  half: "sm:col-span-3",
  full: "sm:col-span-6",
};

type FieldProps = {
  /** Id shared by the label's `htmlFor` and the control it wraps. */
  id: string;
  label: string;
  /** Renders the muted "Optional" marker beside the label. */
  optional?: boolean;
  /** Columns of the six-column grid to take. Defaults to half a two-up row. */
  span?: FieldSpan;
  children: ReactNode;
};

/**
 * One labelled control in the details grid, taking a third, a half or a whole
 * row. The label is a fixed height so every column in a row starts its control
 * on the same line, whether or not it carries the optional marker.
 */
function Field({ id, label, optional, span = "half", children }: FieldProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", FIELD_SPAN_CLASS[span])}>
      <Label className="h-5 gap-1.5 text-sm font-medium" htmlFor={id}>
        {label}
        {optional ? (
          <span className="text-caption leading-none font-normal text-muted-foreground/70">
            Optional
          </span>
        ) : null}
      </Label>
      {children}
    </div>
  );
}

/**
 * A radiogroup of choice cards with the traversal a radiogroup owes its user,
 * meaning one tab stop on the selected card and arrow keys moving selection and
 * focus together. Both persona questions render through it, so neither can drift
 * into being announced as a radiogroup while sitting inert.
 */
function ChoiceCardGroup<T extends string>({
  label,
  labelId,
  options,
  icons,
  value,
  onSelect,
  disabled,
  variant,
}: ChoiceCardGroupProps<T>) {
  const cardRefs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});

  /** Moves the choice with the arrow keys, per the radiogroup contract. */
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta = ["ArrowDown", "ArrowRight"].includes(event.key)
      ? 1
      : ["ArrowUp", "ArrowLeft"].includes(event.key)
        ? -1
        : 0;
    if (delta === 0 || disabled) return;
    event.preventDefault();
    const current = options.findIndex((option) => option.value === value);
    const from = current === -1 ? 0 : current;
    const next = options[(from + delta + options.length) % options.length];
    if (!next) return;
    onSelect(next.value);
    cardRefs.current[next.value]?.focus();
  };

  return (
    <div className="flex flex-col gap-2">
      {labelId ? (
        <p id={labelId} className="text-sm font-medium">
          {label}
        </p>
      ) : null}
      <div
        role="radiogroup"
        aria-label={labelId ? undefined : label}
        aria-labelledby={labelId}
        onKeyDown={handleKeyDown}
        className={
          variant === "list"
            ? "divide-y divide-input overflow-hidden rounded-xl border border-input"
            : "grid gap-3 sm:grid-cols-2"
        }
      >
        {options.map((option, index) => {
          const selected = value === option.value;
          const Icon: LucideIcon = icons[option.value];
          return (
            <button
              key={option.value}
              ref={(node) => {
                cardRefs.current[option.value] = node;
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              // Roving tabindex: one stop for the group, on the selected card.
              tabIndex={selected || (!value && index === 0) ? 0 : -1}
              disabled={disabled}
              onClick={() => onSelect(option.value)}
              className={cn(
                "text-left transition-colors",
                "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
                "disabled:cursor-not-allowed disabled:opacity-60",
                variant === "list"
                  ? cn(
                      // w-full, or the row shrinks to its text and the divide-y
                      // rule between rows stops halfway across the list.
                      "flex w-full items-start gap-3 px-4 py-3",
                      selected ? "bg-accent/40" : "hover:bg-accent/25",
                    )
                  : cn(
                      "flex h-full flex-col rounded-xl border",
                      variant === "tall" ? "min-h-58 gap-2 p-5" : "gap-1.5 p-4",
                      selected
                        ? "border-primary ring-1 ring-primary"
                        : "border-input hover:bg-accent/40",
                    ),
              )}
            >
              {variant === "list" ? (
                // A real radio mark: a row in a list has no selected border to
                // read the choice off.
                <span
                  aria-hidden
                  className={cn(
                    "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
                    selected ? "border-foreground" : "border-input",
                  )}
                >
                  {selected ? <span className="size-2 rounded-full bg-foreground" /> : null}
                </span>
              ) : (
                <span
                  className={cn(
                    "flex items-center justify-center rounded-lg",
                    variant === "tall" ? "size-8" : "size-7",
                    selected ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                  )}
                >
                  <Icon className="size-4" strokeWidth={1.8} />
                </span>
              )}
              <span
                className={cn(variant === "list" ? "flex min-w-0 flex-col gap-0.5" : "contents")}
              >
                <span className="text-sm font-semibold tracking-tight">{option.title}</span>
                <span className="text-body text-muted-foreground">{option.description}</span>
                {option.audience ? (
                  <span className="text-caption text-muted-foreground/70">{option.audience}</span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The progress marker above each step's heading, one pill per step this flow
 * has, filled up to the current one. The current step is named rather than
 * counted, because the two answers reach different second steps and a count
 * would say nothing about which one is coming.
 *
 * @param steps the steps this flow runs, in order
 * @param step the step on screen
 */
function StepProgress({ steps, step }: { steps: readonly Step[]; step: Step }) {
  const reached = steps.indexOf(step);

  return (
    <div className="mb-3 flex items-center gap-1.5">
      {steps.map((each, index) => (
        <span
          key={each}
          className={cn(
            "h-0.5 w-7 rounded-full",
            index <= reached ? "bg-foreground" : "bg-border",
          )}
        />
      ))}
      <span className="ml-1 text-meta text-muted-foreground">{STEP_LABEL[step]}</span>
    </div>
  );
}

/**
 * Names a workspace and asks the one question that decides its workflow, own
 * records or other people's records, then does whatever that answer implies.
 * Every new workspace runs it, at signup and from the switcher alike, because
 * the persona belongs to the workspace rather than to the account. A workspace
 * that already holds several people's records skips straight to the details.
 */
export function PersonaWorkspaceForm({
  existingWorkspace,
  existingPatientCount = 0,
  onCompleted,
  onCancel,
}: Props) {
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const isBackfill = !!existingWorkspace;
  const skipsPersonaQuestion = isBackfill && existingPatientCount >= 2;

  // Creating starts with a name while legacy workspaces start with the identity question.
  const [step, setStep] = useState<Step>(
    skipsPersonaQuestion ? STEP.DETAILS : STEP.WORKSPACE,
  );
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [requesterType, setRequesterType] = useState<RequesterType | null>(null);
  const [orgName, setOrgName] = useState(existingWorkspace?.name ?? "");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [stateRegion, setStateRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [personName, setPersonName] = useState(session?.user?.name ?? "");
  const [designation, setDesignation] = useState<Designation | "">("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const workspaceCreated = useRef(false);
  const steps: readonly Step[] =
    accountType === ACCOUNT_TYPE.INDIVIDUAL
      ? [STEP.WORKSPACE, STEP.CONSENT]
      : [STEP.WORKSPACE, STEP.DETAILS];

  /** Refreshes the workspace, persona and session queries, then tells the caller setup is done. */
  const finishSetup = () => {
    queryClient.invalidateQueries({ queryKey: orpc.org.listOrganizations.key() });
    queryClient.invalidateQueries({ queryKey: orpc.org.getFullOrganization.key() });
    queryClient.invalidateQueries({ queryKey: orpc.org.getActiveOrganization.key() });
    queryClient.invalidateQueries({ queryKey: orpc.org.getActorContext.key() });
    // better-auth's own session query, so the header picks up a renamed member.
    queryClient.invalidateQueries({ queryKey: [["session"]] });
    if (onCompleted) onCompleted();
  };

  /** Surfaces a failed mutation in the form instead of a toast. */
  const fail = (e: unknown) => {
    setError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
  };

  // No onSuccess anywhere — each submit awaits its own calls in order, then refreshes once.
  const updateNameMutation = useMutation(orpc.org.updateMyName.mutationOptions());
  const individualMutation = useMutation(orpc.org.createIndividualWorkspace.mutationOptions());
  const pairMutation = useMutation(orpc.org.createWorkspacePair.mutationOptions());
  const personaMutation = useMutation(orpc.org.setOrgPersona.mutationOptions());
  const acceptTermsMutation = useMutation(orpc.onboarding.acceptTerms.mutationOptions());

  /** Narrows the Select's plain string back to a known designation. */
  const pickDesignation = (value: string) => {
    setDesignation(DESIGNATIONS.find((option) => option === value) ?? "");
  };

  /**
   * Submits the self-records form, which ends on the consent step. The workspace
   * and the owner's patient membership both have to exist before the consent row
   * can point at them, so the three calls run in that order. Safe to press again
   * after a failure, since each call is idempotent or skipped once it has run.
   */
  const submitSelfRecordsForm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      if (existingWorkspace) {
        await personaMutation.mutateAsync({
          organizationId: existingWorkspace.id,
          accountType: ACCOUNT_TYPE.INDIVIDUAL,
        });
      } else if (!workspaceCreated.current) {
        await individualMutation.mutateAsync({ name: orgName.trim() || undefined });
        workspaceCreated.current = true;
      }
      // Both branches leave the individual workspace active, so these resolve against it.
      await orpcClient.org.ensureSelfPatient({});
      await acceptTermsMutation.mutateAsync({});
    } catch (e) {
      setSubmitting(false);
      fail(e);
      return;
    }
    finishSetup();
  };

  /**
   * Submits the whose-records form. This one only advances, with no server call.
   * A backfill has no name field, so only a new workspace has a name to
   * validate. Self records go on to the consent step, other people's records to
   * the details step.
   */
  const proceedNextStep = () => {
    if (!isBackfill && !orgName.trim()) {
      setError("Please name your workspace.");
      return;
    }
    if (!accountType) return;
    setError(null);
    setStep(accountType === ACCOUNT_TYPE.INDIVIDUAL ? STEP.CONSENT : STEP.DETAILS);
  };

  /** Takes the sub-persona answer, dropping a role an individual is never asked for. */
  const pickRequesterType = (value: RequesterType) => {
    setError(null);
    setRequesterType(value);
    if (value === REQUESTER_TYPE.PERSON) setDesignation("");
  };

  /** Submits the other-records form, validating the details before it creates or updates the workspace. */
  const submitOtherRecordsForm = async () => {
    setError(null);
    const name = orgName.trim();
    const trimmedPersonName = personName.trim();
    const trimmedStreet = street.trim();
    const trimmedCity = city.trim();
    const trimmedStateRegion = stateRegion.trim();
    const trimmedPostalCode = postalCode.trim();
    const trimmedCountry = country.trim();
    const rolePicked = designation || undefined;

    if (!requesterType) {
      setError("Please tell us whether you are an individual or an organization.");
      return;
    }
    if (!name) {
      setError("Please go back and name your workspace.");
      return;
    }
    if (!trimmedPersonName) {
      setError("Please enter your name.");
      return;
    }
    if (requesterType === REQUESTER_TYPE.ORGANIZATION && !rolePicked) {
      setError("Please pick your role.");
      return;
    }
    if (!trimmedStreet) {
      setError("Please enter the street address.");
      return;
    }
    if (!trimmedCity) {
      setError("Please enter the city.");
      return;
    }
    if (!trimmedPostalCode) {
      setError("Please enter the postal code.");
      return;
    }
    if (!trimmedCountry) {
      setError("Please enter the country.");
      return;
    }

    const address = {
      street: trimmedStreet,
      city: trimmedCity,
      stateRegion: trimmedStateRegion || undefined,
      postalCode: trimmedPostalCode,
      country: trimmedCountry,
    };

    setSubmitting(true);
    try {
      if (trimmedPersonName !== (session?.user?.name ?? "")) {
        await updateNameMutation.mutateAsync({ name: trimmedPersonName });
      }

      if (existingWorkspace) {
        await personaMutation.mutateAsync({
          organizationId: existingWorkspace.id,
          accountType: ACCOUNT_TYPE.ORGANIZATION,
          requesterType,
          address,
          designation: rolePicked,
        });
      } else {
        await pairMutation.mutateAsync({
          name,
          slug: generateUniqueSlug(name),
          requesterType,
          address,
          designation: rolePicked,
        });
      }
    } catch (e) {
      setSubmitting(false);
      fail(e);
      return;
    }
    finishSetup();
  };

  const chosenOption = PERSONA_OPTIONS.find((option) => option.value === accountType);
  const setupCopy = workspaceSetupCopy(isBackfill);
  const workspaceName = (isBackfill ? existingWorkspace?.name : orgName)?.trim() ?? "";
  // Name and role sit on one line; with no role to pair with, the name takes it.
  const nameSpan: FieldSpan = requesterType === REQUESTER_TYPE.ORGANIZATION ? "half" : "full";

  if (step === STEP.CONSENT) {
    return (
      <div className="flex w-full flex-col">
        <StepProgress steps={steps} step={STEP.CONSENT} />
        {/* Names the party receiving the records, since this is the agreement
            to hand them over and no other party is involved. */}
        <h1 className="text-xl leading-tight font-bold tracking-tight">
          Share your records with Medblocks Platform
        </h1>

        {/* `ownRecords`, so the copy never says another party is asking. In this
            workflow the reader is the patient and the records are their own. */}
        <ConsentDisclosure ownRecords />

        <form
          className="mt-6 flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submitSelfRecordsForm();
          }}
        >
          <ConsentTermsLine />

          {error ? (
            <p role="alert" className="text-body text-destructive">
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {/* Read during render on purpose: going back to rename a workspace
                that already exists would drop the new name silently. */}
            {workspaceCreated.current ? null : (
              <Button
                type="button"
                variant="outline"
                className="h-9 rounded-lg px-4"
                disabled={submitting}
                onClick={() => {
                  setError(null);
                  setStep(STEP.WORKSPACE);
                }}
              >
                Back
              </Button>
            )}
            <Button type="submit" className="h-9 w-44 rounded-lg" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" data-icon="inline-start" />
                  {setupCopy.submitting}
                </>
              ) : (
                "Agree and continue"
              )}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  if (step === STEP.DETAILS) {
    return (
      <div className="flex w-full flex-col">
        {skipsPersonaQuestion ? null : <StepProgress steps={steps} step={STEP.DETAILS} />}
        <h1 className="text-xl leading-tight font-bold tracking-tight">{setupCopy.heading}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2.5 py-1">
            <Building2 className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate text-body font-medium">{workspaceName}</span>
          </span>
          {chosenOption ? (
            <span className="text-body text-muted-foreground">{chosenOption.title}</span>
          ) : null}
        </div>

        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submitOtherRecordsForm();
          }}
        >
          <ChoiceCardGroup
            label={REQUESTER_QUESTION}
            labelId="persona-requester-question"
            options={REQUESTER_OPTIONS}
            icons={REQUESTER_ICON}
            value={requesterType}
            onSelect={pickRequesterType}
            disabled={submitting}
            variant="list"
          />

          {requesterType ? (
            <div className={DETAILS_GRID_CLASS}>
              <Field id="persona-person-name" label="Your name" span={nameSpan}>
                <Input
                  id="persona-person-name"
                  autoComplete="name"
                  className={FIELD_CONTROL_CLASS}
                  value={personName}
                  disabled={submitting}
                  onChange={(e) => setPersonName(e.target.value)}
                  placeholder="Your full name"
                />
              </Field>

              {requesterType === REQUESTER_TYPE.ORGANIZATION ? (
                <Field id="persona-designation" label="Your role" span="half">
                  <Select value={designation} disabled={submitting} onValueChange={pickDesignation}>
                    <SelectTrigger
                      id="persona-designation"
                      className="w-full rounded-lg text-body data-[size=default]:h-9"
                    >
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {DESIGNATIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              ) : null}

              <Field
                id="persona-street"
                label={requesterAddressHeading(requesterType)}
                span="full"
              >
                <Input
                  id="persona-street"
                  autoComplete="street-address"
                  className={FIELD_CONTROL_CLASS}
                  value={street}
                  disabled={submitting}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="1600 Pearl Street, Suite 200"
                />
              </Field>

              <Field id="persona-city" label="City" span="half">
                <Input
                  id="persona-city"
                  autoComplete="address-level2"
                  className={FIELD_CONTROL_CLASS}
                  value={city}
                  disabled={submitting}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Boulder"
                />
              </Field>

              <Field id="persona-state-region" label="State or region" span="half" optional>
                <Input
                  id="persona-state-region"
                  autoComplete="address-level1"
                  className={FIELD_CONTROL_CLASS}
                  value={stateRegion}
                  disabled={submitting}
                  onChange={(e) => setStateRegion(e.target.value)}
                  placeholder="Colorado"
                />
              </Field>

              <Field id="persona-postal-code" label="Postal code" span="half">
                <Input
                  id="persona-postal-code"
                  autoComplete="postal-code"
                  className={FIELD_CONTROL_CLASS}
                  value={postalCode}
                  disabled={submitting}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="80301"
                />
              </Field>

              <Field id="persona-country" label="Country" span="half">
                <Input
                  id="persona-country"
                  autoComplete="country-name"
                  className={FIELD_CONTROL_CLASS}
                  value={country}
                  disabled={submitting}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="United States"
                />
              </Field>
            </div>
          ) : null}

          {error ? (
            <p role="alert" className="text-body text-destructive">
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {skipsPersonaQuestion ? null : (
              <Button
                type="button"
                variant="outline"
                className="h-9 rounded-lg px-4"
                disabled={submitting}
                onClick={() => {
                  setError(null);
                  setStep(STEP.WORKSPACE);
                }}
              >
                Back
              </Button>
            )}
            <Button
              type="submit"
              className="h-9 w-36 rounded-lg"
              disabled={submitting || !requesterType}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" data-icon="inline-start" />
                  {setupCopy.submitting}
                </>
              ) : (
                setupCopy.submit
              )}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col">
      <StepProgress steps={steps} step={STEP.WORKSPACE} />
      <h1 className="text-xl leading-tight font-bold tracking-tight">
        {isBackfill ? PERSONA_QUESTION : WORKSPACE_NAME_QUESTION}
      </h1>
      <p className="mt-1 text-body text-muted-foreground">
        {isBackfill ? PERSONA_SUBHEADING : WORKSPACE_NAME_SUBHEADING}
      </p>

      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          proceedNextStep();
        }}
      >
        {/* A backfill cannot rename its workspace, so it goes straight to the
            question, which names the workspace it already has. */}
        {isBackfill ? null : (
          <div className={DETAILS_GRID_CLASS}>
            <Field id="persona-workspace-name" label={WORKSPACE_NAME_LABEL} span="full">
              <Input
                id="persona-workspace-name"
                autoComplete="organization"
                autoFocus
                className={FIELD_CONTROL_CLASS}
                value={orgName}
                disabled={submitting}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Acme Health, or the Robert family"
              />
            </Field>
          </div>
        )}

        <ChoiceCardGroup
          label={PERSONA_QUESTION}
          labelId={isBackfill ? undefined : "persona-account-question"}
          options={PERSONA_OPTIONS}
          icons={PERSONA_ICON}
          value={accountType}
          onSelect={(value) => {
            setError(null);
            setAccountType(value);
          }}
          disabled={submitting}
          variant="tall"
        />

        {error ? (
          <p role="alert" className="text-body text-destructive">
            {error}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {onCancel ? (
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-lg px-4"
              disabled={submitting}
              onClick={onCancel}
            >
              Cancel
            </Button>
          ) : null}
          <Button
            type="submit"
            className="h-9 w-36 rounded-lg"
            disabled={submitting || !accountType || (!isBackfill && !orgName.trim())}
          >
            {/* Step one only advances now, so it has no in-flight state to show. */}
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
}
